import { FormEvent, useEffect, useState } from 'react';
import { ApiError, api } from '../../lib/api';

interface Counts {
  users: number;
  activeTokens: number;
  events: number;
  openTodos: number;
  inboxCards: number;
  unreadInboxCards: number;
}
interface AgentStats {
  runningRuns: number;
  failedRuns: number;
  totalTokens: number;
  averageLatencyMs: number;
}
interface AiConfig {
  baseUrl: string;
  model: string;
  maxTokens: number;
  temperature: number;
  hasKey: boolean;
  keySource: string;
}
interface WhaleuStatus {
  databasePath: string;
  available: boolean;
  reason?: string;
  sources: number;
  entries: number;
  sizeBytes?: number;
  modifiedAt?: string;
}
interface AdminUser {
  id: string;
  numId?: number;
  email: string;
  displayName: string;
  school: string;
  grade: string;
  major: string;
  createdAt?: string;
  lastLoginAt?: string;
}
interface Overview { counts: Counts; agent: AgentStats; ai: AiConfig; whaleu: WhaleuStatus }

function formatBytes(value = 0) {
  if (!value) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  const index = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
  return `${(value / (1024 ** index)).toFixed(index ? 1 : 0)} ${units[index]}`;
}

export function AdminPage() {
  const [overview, setOverview] = useState<Overview>();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [selected, setSelected] = useState<AdminUser>();
  const [ai, setAi] = useState<AiConfig>();
  const [whaleu, setWhaleu] = useState<WhaleuStatus>();
  const [apiKey, setApiKey] = useState('');
  const [notice, setNotice] = useState({ target: 'all', title: '', summary: '', conf: 'mid' });
  const [status, setStatus] = useState('正在读取服务器状态…');
  const [forbidden, setForbidden] = useState(false);

  const load = async () => {
    setStatus('正在读取服务器状态…');
    try {
      const [overviewResponse, usersResponse] = await Promise.all([
        api<{ data: Overview }>('/api/admin/overview'),
        api<{ data: AdminUser[] }>('/api/admin/users'),
      ]);
      setOverview(overviewResponse.data);
      setAi(overviewResponse.data.ai);
      setWhaleu(overviewResponse.data.whaleu);
      setUsers(usersResponse.data);
      setSelected((current) => current ? usersResponse.data.find((user) => user.id === current.id) : usersResponse.data[0]);
      setStatus(`状态更新于 ${new Date().toLocaleTimeString('zh-CN', { hour12: false })}`);
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        window.location.assign('/login?next=/admin');
        return;
      }
      if (error instanceof ApiError && error.status === 403) setForbidden(true);
      setStatus(error instanceof Error ? error.message : '服务器状态读取失败');
    }
  };

  useEffect(() => { void load(); }, []);

  const saveAi = async (event: FormEvent) => {
    event.preventDefault();
    if (!ai) return;
    setStatus('正在保存 AI 配置…');
    try {
      const response = await api<{ data: AiConfig }>('/api/admin/ai-config', {
        method: 'PUT', body: JSON.stringify({ ...ai, apiKey: apiKey || undefined }),
      });
      setAi(response.data); setApiKey(''); setStatus('AI 配置已生效，新会话无需重启 Agent。');
    } catch (error) { setStatus(error instanceof Error ? error.message : 'AI 配置保存失败'); }
  };

  const saveWhaleu = async (event: FormEvent) => {
    event.preventDefault();
    if (!whaleu) return;
    setStatus('正在检查 WhaleU 信库…');
    try {
      const response = await api<{ data: WhaleuStatus }>('/api/admin/whaleu', {
        method: 'PUT', body: JSON.stringify({ databasePath: whaleu.databasePath }),
      });
      setWhaleu(response.data); setStatus(response.data.available ? 'WhaleU 信库连接正常。' : `信库不可用：${response.data.reason || '结构不完整'}`);
    } catch (error) { setStatus(error instanceof Error ? error.message : '信库检查失败'); }
  };

  const saveUser = async (event: FormEvent) => {
    event.preventDefault();
    if (!selected) return;
    try {
      const response = await api<{ data: AdminUser }>(`/api/admin/users/${encodeURIComponent(selected.id)}`, {
        method: 'PATCH', body: JSON.stringify(selected),
      });
      setSelected(response.data);
      setUsers((current) => current.map((user) => user.id === response.data.id ? response.data : user));
      setStatus(`已更新用户 ${response.data.email}`);
    } catch (error) { setStatus(error instanceof Error ? error.message : '用户更新失败'); }
  };

  const sendNotice = async (event: FormEvent) => {
    event.preventDefault();
    try {
      const response = await api<{ data: { delivered: number } }>('/api/admin/notifications', {
        method: 'POST', body: JSON.stringify(notice),
      });
      setNotice((current) => ({ ...current, title: '', summary: '' }));
      setStatus(`通知已投递给 ${response.data.delivered} 个账户。`);
      void load();
    } catch (error) { setStatus(error instanceof Error ? error.message : '通知发送失败'); }
  };

  if (forbidden) return <div className="content-column admin-denied"><p>CAIGEN · SERVER</p><h1>这里需要管理员权限</h1><span>通过 <code>CAIGEN_ADMIN_EMAILS</code> 配置管理员邮箱，重新登录后进入。</span></div>;

  const counts = overview?.counts;
  return <div className="content-column admin-page">
    <header className="page-header admin-header"><div><p>CAIGEN · SERVER DASHBOARD</p><h1>服务器管理台</h1><span>{status}</span></div><button className="quiet-button" type="button" onClick={() => void load()}>刷新状态</button></header>

    <section className="admin-metrics" aria-label="服务指标">
      <article><small>账户</small><strong>{counts?.users ?? '—'}</strong><span>{counts?.activeTokens ?? 0} 个有效会话</span></article>
      <article><small>日历数据</small><strong>{counts ? counts.events + counts.openTodos : '—'}</strong><span>{counts?.openTodos ?? 0} 个未完成待办</span></article>
      <article><small>通知</small><strong>{counts?.unreadInboxCards ?? '—'}</strong><span>{counts?.inboxCards ?? 0} 条累计投递</span></article>
      <article><small>Agent</small><strong>{overview?.agent.totalTokens ?? '—'}</strong><span>{overview?.agent.averageLatencyMs ?? 0}ms 平均耗时</span></article>
    </section>

    <div className="admin-grid">
      <form className="paper-card admin-panel" onSubmit={saveAi}>
        <header><div><small>AI RUNTIME</small><h2>模型配置</h2></div><span className={ai?.hasKey ? 'service-ok' : 'service-error'}>{ai?.hasKey ? '已配置' : '缺少密钥'}</span></header>
        <label>API Base URL<input value={ai?.baseUrl || ''} onChange={(event) => setAi((current) => current ? { ...current, baseUrl: event.target.value } : current)} /></label>
        <label>模型<input value={ai?.model || ''} onChange={(event) => setAi((current) => current ? { ...current, model: event.target.value } : current)} /></label>
        <div className="admin-fields"><label>Max tokens<input type="number" min="256" max="32768" value={ai?.maxTokens || 4096} onChange={(event) => setAi((current) => current ? { ...current, maxTokens: Number(event.target.value) } : current)} /></label><label>Temperature<input type="number" min="0" max="2" step="0.1" value={ai?.temperature ?? 0.3} onChange={(event) => setAi((current) => current ? { ...current, temperature: Number(event.target.value) } : current)} /></label></div>
        <label>替换 API key<input type="password" value={apiKey} onChange={(event) => setApiKey(event.target.value)} placeholder={ai?.hasKey ? `已配置 · ${ai.keySource}` : '输入后仅保存到服务器 secret 文件'} autoComplete="new-password" /></label>
        <p className="admin-hint">这里的状态只表示服务器已持有密钥；实际可用性以 Agent 发起请求的结果为准。</p>
        <button className="primary-button">保存并应用</button>
      </form>

      <form className="paper-card admin-panel" onSubmit={saveWhaleu}>
        <header><div><small>WHALEU KNOWLEDGE</small><h2>信库检查</h2></div><span className={whaleu?.available ? 'service-ok' : 'service-error'}>{whaleu?.available ? '可用' : '不可用'}</span></header>
        <label>knowledge.db 绝对路径<textarea rows={3} value={whaleu?.databasePath || ''} onChange={(event) => setWhaleu((current) => current ? { ...current, databasePath: event.target.value } : current)} /></label>
        <dl className="admin-kv"><div><dt>来源</dt><dd>{whaleu?.sources ?? 0}</dd></div><div><dt>条目</dt><dd>{whaleu?.entries ?? 0}</dd></div><div><dt>大小</dt><dd>{formatBytes(whaleu?.sizeBytes)}</dd></div><div><dt>更新时间</dt><dd>{whaleu?.modifiedAt?.slice(0, 16).replace('T', ' ') || '—'}</dd></div></dl>
        {whaleu?.reason ? <p className="admin-warning">{whaleu.reason}</p> : null}
        <button className="primary-button">保存并重新检查</button>
      </form>

      <section className="paper-card admin-panel admin-users">
        <header><div><small>ACCOUNT DIRECTORY</small><h2>用户管理</h2></div><span>{users.length} 个账户</span></header>
        <div className="admin-user-layout"><nav>{users.map((user) => <button type="button" className={selected?.id === user.id ? 'active' : ''} key={user.id} onClick={() => setSelected(user)}><b>{user.displayName || user.email}</b><small>{user.email}</small></button>)}</nav>
        {selected ? <form onSubmit={saveUser}><label>显示名称<input value={selected.displayName} onChange={(event) => setSelected((current) => current ? { ...current, displayName: event.target.value } : current)} /></label><label>学校<input value={selected.school} onChange={(event) => setSelected((current) => current ? { ...current, school: event.target.value } : current)} /></label><div className="admin-fields"><label>年级<input value={selected.grade} onChange={(event) => setSelected((current) => current ? { ...current, grade: event.target.value } : current)} /></label><label>专业<input value={selected.major} onChange={(event) => setSelected((current) => current ? { ...current, major: event.target.value } : current)} /></label></div><p>{selected.id} · 最近登录 {selected.lastLoginAt?.slice(0, 16).replace('T', ' ') || '从未'}</p><button className="primary-button">保存用户</button></form> : null}</div>
      </section>

      <form className="paper-card admin-panel admin-notice" onSubmit={sendNotice}>
        <header><div><small>ADMIN NOTIFICATION</small><h2>用户通知</h2></div><span>进入通知收件箱</span></header>
        <label>接收账户<select value={notice.target} onChange={(event) => setNotice((current) => ({ ...current, target: event.target.value }))}><option value="all">全部账户</option>{users.map((user) => <option value={user.id} key={user.id}>{user.displayName || user.email}</option>)}</select></label>
        <label>标题<input required value={notice.title} onChange={(event) => setNotice((current) => ({ ...current, title: event.target.value }))} /></label>
        <label>正文<textarea required rows={5} value={notice.summary} onChange={(event) => setNotice((current) => ({ ...current, summary: event.target.value }))} /></label>
        <label>重要度<select value={notice.conf} onChange={(event) => setNotice((current) => ({ ...current, conf: event.target.value }))}><option value="hi">重要</option><option value="mid">一般</option><option value="low">低</option></select></label>
        <button className="primary-button">投递通知</button>
      </form>
    </div>
  </div>;
}
